import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import SummaryService from '../../../services/api/SummaryService'
import { CHANNELS, CRON_SPECIAL, DEFAULT_CRON, FREQUENCIES, GENERATORS, OUTPUT_FORMATS } from '../constants'
import {
  INSTANT_GENERATORS,
  allowedGeneratorsFor,
  scrollToFirstError,
  DEFAULT_FORM_DATA,
  buildFormDataFromSummary,
  supportsExportAll,
  supportsExternalEmails,
  validateFormData,
  buildPayload
} from '../summaryFormUtils'

export const useSummaryForm = ({
  collectId,
  summary,
  formOptions,
  onSuccess
}) => {
  const [formData, setFormData] = useState(
    summary ? buildFormDataFromSummary(summary) : DEFAULT_FORM_DATA
  )
  const formDataRef = useRef(formData)
  formDataRef.current = formData
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})
  const [customMode, setCustomMode] = useState(() => {
    if (!summary?.cron_expression || !formOptions?.recurrencePresets)
      return false
    const cron = summary.cron_expression
    return !formOptions.recurrencePresets.some(preset => preset.value === cron)
  })

  useEffect(() => {
    if (summary)
      setFormData(buildFormDataFromSummary(summary))
  }, [summary])

  useEffect(() => {
    if (supportsExportAll(formData, formOptions?.collectFrequency) || !formData.exportAll)
      return
    setFormData(previous => ({ ...previous, exportAll: false }))
  }, [formData.generator, formData.cronExpression, formData.exportAll, formOptions?.collectFrequency])

  useEffect(() => {
    const noExternalEmailsState = !formData.useExternalEmails && !formData.variableId
    if (supportsExternalEmails(formData) || noExternalEmailsState)
      return
    setFormData(previous => ({ ...previous, useExternalEmails: false, variableId: null }))
  }, [
    formData.generator,
    formData.cronExpression,
    formData.channel,
    formData.useExternalEmails,
    formData.variableId
  ])

  useEffect(() => {
    if (formData.useExternalEmails || !formData.variableId)
      return
    setFormData(previous => ({ ...previous, variableId: null }))
  }, [formData.useExternalEmails, formData.variableId])

  const updateField = useCallback((field, value) => {
    setFormData(previous => ({ ...previous, [field]: value }))
  }, [])

  const toggleCustomMode = useCallback(enabled => {
    setCustomMode(enabled)
  }, [])

  const handleGeneratorChange = useCallback(option => {
    const generator = option.value
    setFormData(previous => {
      const updates = { generator }
      if (generator === GENERATORS.COLLECT)
        updates.currentDraftOnly = true
      else
        updates.currentDraftOnly = false

      if (!INSTANT_GENERATORS.includes(generator)
          && previous.cronExpression === CRON_SPECIAL.INSTANT)
        updates.cronExpression = DEFAULT_CRON

      return { ...previous, ...updates }
    })
  }, [])

  const handleChannelChange = useCallback(option => {
    const channel = option.value
    setFormData(previous => {
      const updates = { channel }
      if (channel === CHANNELS.SFTP
          && previous.cronExpression === CRON_SPECIAL.INSTANT)
        updates.cronExpression = DEFAULT_CRON

      const outputFormat = channel === CHANNELS.SFTP ? previous.outputFormat : OUTPUT_FORMATS.XLSX
      if (outputFormat !== previous.outputFormat)
        updates.outputFormat = outputFormat

      const allowedGenerators = allowedGeneratorsFor(channel, outputFormat)
      if (!allowedGenerators.includes(previous.generator)) {
        const [nextGenerator] = allowedGenerators
        updates.generator = nextGenerator
        updates.currentDraftOnly = nextGenerator === GENERATORS.COLLECT
      }

      return { ...previous, ...updates }
    })
  }, [])

  const availableGenerators = useMemo(() => {
    if (!formOptions)
      return []
    const allowed = allowedGeneratorsFor(formData.channel, formData.outputFormat)
    return formOptions.generators.filter(([, key]) => allowed.includes(key))
  }, [formOptions, formData.channel, formData.outputFormat])

  const availableRecurrences = useMemo(() => {
    if (!formOptions)
      return []
    return formOptions.recurrencePresets.filter(preset => {
      if (preset.value === CRON_SPECIAL.INSTANT) {
        return INSTANT_GENERATORS.includes(formData.generator)
          && formData.channel !== CHANNELS.SFTP
      }
      return true
    })
  }, [formOptions, formData.generator, formData.channel])

  const isInstant = formData.cronExpression === CRON_SPECIAL.INSTANT
  const isSftp = formData.channel === CHANNELS.SFTP
  const isRecurrent = formOptions?.collectFrequency === FREQUENCIES.RECURRENT

  const showSections = formData.generator === GENERATORS.SECTION
  const showSftpOptions = isSftp
  const showExternalEmails = supportsExternalEmails(formData)
  const showPeriodOnly = formData.generator === GENERATORS.RESOURCE
    && !isInstant && formOptions?.collectFrequency === FREQUENCIES.SPONTANEOUS
  const showCustomCron = customMode
  const showCurrentDraftOnly = formData.generator === GENERATORS.COLLECT && isRecurrent
  const showPriorityResources = isRecurrent

  const handleSubmit = useCallback(async filtersAttributes => {
    const { current } = formDataRef
    const validationErrors = validateFormData(current)
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      scrollToFirstError()
      return
    }

    setSubmitting(true)
    setErrors({})
    const payload = buildPayload(current, filtersAttributes)

    try {
      let response = null
      if (summary)
        response = await SummaryService.updateSummary(collectId, summary.id, payload)
      else
        response = await SummaryService.createSummary(collectId, payload)

      if (response.success)
        onSuccess(response)
      else {
        setErrors(response.formatted_errors || {})
        scrollToFirstError()
      }
    } catch (error) {
      setErrors({ base: [error.message] })
    } finally {
      setSubmitting(false)
    }
  }, [collectId, summary, onSuccess])

  return {
    formData,
    updateField,
    handleGeneratorChange,
    handleChannelChange,
    handleSubmit,
    submitting,
    errors,
    availableGenerators,
    availableRecurrences,
    showSections,
    showSftpOptions,
    showExternalEmails,
    showPeriodOnly,
    showCustomCron,
    toggleCustomMode,
    showCurrentDraftOnly,
    showPriorityResources
  }
}

export default useSummaryForm
